
import { useNavigate } from 'react-router-dom';

interface CartProps {
  isOpen: boolean;
  onClose: () => void;
  items: any[];
  onRemoveItem: (id: string) => void;
  onUpdateQuantity: (id: string, quantity: number) => void;
  totalPrice: number;
}

export default function Cart({ isOpen, onClose, items, onRemoveItem, onUpdateQuantity, totalPrice }: CartProps) {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const MINIMUM_ORDER_VALUE = 1200; // CHF 1200 Mindestbestellwert
  
  // NEUE SCHWEIZER GEBÜHREN-BERECHNUNG
  const HANDLING_FEE = 27.00; // CHF 27.00 Handling
  const DELIVERY_FEE = 0.00; // Abholung oder nach Absprache
  const VAT_RATE = 0.081; // 8.1% MwSt (Schweiz)
  
  const netTotal = totalPrice + HANDLING_FEE + DELIVERY_FEE;
  const vatAmount = netTotal * VAT_RATE;
  const grandTotal = netTotal + vatAmount;
  
  const remainingAmount = Math.max(0, MINIMUM_ORDER_VALUE - totalPrice);
  const canProceedToCheckout = totalPrice >= MINIMUM_ORDER_VALUE;

  const handleCheckout = () => {
    if (canProceedToCheckout) {
      navigate('/checkout', {
        state: {
          items: items,
          totalPrice: totalPrice
        }
      });
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Mobile-optimierter Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      
      {/* Mobile-optimiertes Cart Panel */}
      <div className="absolute right-0 top-0 h-full w-full sm:w-96 bg-white shadow-xl flex flex-col">
        {/* Mobile-optimierter Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Warenkorb</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl sm:text-2xl"></i>
          </button>
        </div>

        {/* Mobile-optimierter Content */}
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-6 sm:p-8 text-center">
              <i className="ri-shopping-cart-line text-4xl sm:text-6xl text-gray-300 mb-4"></i>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-600 mb-2">Warenkorb ist leer</h3>
              <p className="text-sm sm:text-base text-gray-500">Fügen Sie Produkte hinzu, um zu bestellen</p>
            </div>
          ) : (
            <div className="p-4 sm:p-6 space-y-6">
              {items.map((item) => (
                <div key={item.id} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center space-x-3">
                    {/* Produktbild - kleiner */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover object-center rounded-md border border-gray-200"
                      />
                    </div>

                    {/* Produktinfo - kompakter */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm leading-tight mb-1">
                        {item.name}
                      </h3>
                      <div className="space-y-0.5 mb-2">
                        <div className="font-medium text-orange-600 text-xs">
                          CHF {item.price.toFixed(2)} pro Verpackungseinheit
                        </div>
                        <div className="text-xs text-gray-500">
                          ({item.packagingUnit || item.stueck_pro_ve || item.stueckProVE || 1} Stück pro Verpackungseinheit)
                        </div>
                      </div>

                      {/* Mengensteuerung und Preis - kompakter */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
                            className="w-7 h-7 rounded-full bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center transition-all cursor-pointer"
                          >
                            <i className="ri-subtract-line text-gray-600 text-sm"></i>
                          </button>
                          <div className="bg-white border border-gray-300 rounded px-3 py-1 min-w-[45px] text-center">
                            <span className="font-medium text-gray-900 text-sm">{item.quantity}</span>
                          </div>
                          <button
                            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
                            className="w-7 h-7 rounded-full bg-white border border-gray-300 hover:bg-gray-50 flex items-center justify-center transition-all cursor-pointer"
                          >
                            <i className="ri-add-line text-gray-600 text-sm"></i>
                          </button>
                        </div>
                        
                        <div className="text-right">
                          <div className="font-bold text-gray-900 text-sm">
                            CHF {(item.price * item.quantity).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {item.quantity * (item.packagingUnit || item.stueck_pro_ve || item.stueckProVE || 1)} Stück total
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Löschen Button - kleiner */}
                    <div className="flex-shrink-0">
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="w-7 h-7 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-full transition-all cursor-pointer flex items-center justify-center"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* NEUE Mobile-optimierte Footer mit Schweizer Gebühren */}
        {items.length > 0 && (
          <div className="border-t border-gray-200 p-4 sm:p-6 bg-white">
            {/* Mobile-optimierte Mindestbestellwert-Anzeige */}
            {!canProceedToCheckout && (
              <div className="mb-4 p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2 text-yellow-800">
                  <i className="ri-information-line text-lg"></i>
                  <div className="flex-1">
                    <div className="font-medium text-sm sm:text-base">Mindestbestellwert: CHF 1200.00</div>
                    <div className="text-xs sm:text-sm">
                      Noch CHF {remainingAmount.toFixed(2)} bis zum Mindestbestellwert
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* NEUE Mobile-optimierte Gesamtsumme mit Schweizer Gebühren */}
            <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
              <div className="flex justify-between items-center text-sm sm:text-base">
                <span className="text-gray-600">Zwischensumme Produkte:</span>
                <span className="font-medium">CHF {totalPrice.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center text-sm sm:text-base">
                <span className="text-gray-600">Handling-Zuschlag:</span>
                <span className="font-medium">CHF {HANDLING_FEE.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center text-sm sm:text-base">
                <span className="text-gray-600">Versand:</span>
                <span className="font-medium text-blue-600">Nach Absprache</span>
              </div>
              
              <div className="flex justify-between items-center text-sm sm:text-base border-t border-gray-200 pt-2">
                <span className="text-gray-600">Netto-Gesamtsumme:</span>
                <span className="font-semibold">CHF {netTotal.toFixed(2)}</span>
              </div>
              
              <div className="flex justify-between items-center text-sm sm:text-base">
                <span className="text-gray-600">MwSt. (8.1%):</span>
                <span className="font-medium">CHF {vatAmount.toFixed(2)}</span>
              </div>
              
              <div className="border-t border-gray-200 pt-2 sm:pt-3">
                <div className="flex justify-between items-center text-base sm:text-lg font-bold">
                  <span>BRUTTO-GESAMTSUMME:</span>
                  <span className="text-orange-600">CHF {grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Mobile-optimierter Checkout Button */}
            <button
              onClick={handleCheckout}
              disabled={!canProceedToCheckout}
              className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-lg font-semibold text-sm sm:text-base transition-all whitespace-nowrap cursor-pointer ${
                canProceedToCheckout
                  ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {canProceedToCheckout ? 'Zur Bestellung' : `Noch CHF ${remainingAmount.toFixed(2)} benötigt`}
            </button>

            {/* NEUER Mobile-optimierter Hinweis */}
            <p className="text-xs sm:text-sm text-gray-500 text-center mt-3 sm:mt-4">
              Alle Preise inkl. 8.1% MwSt & Handling (CHF 27). Versand nach Absprache.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
